import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable, OnDestroy } from '@angular/core';


import { Observable, of } from 'rxjs';

import { LookupService } from '../lookup.service';
import { CategoryService } from '../category.service';
import { StatusService } from '../status.service';
import { UserService } from '../user.service';
import { map } from 'rxjs/operators';
import { LookupTypeService } from '../lookup-type.service';
import {
  enumGroupReportReturnType,
  enumReportCategory,
  enumReportType,
  Report,
  ReportConfig,
  ReportData,
  ReportOptions,
} from '../../models/report';
import { OrganisationService } from '../organisation.service';
import { User } from '../../models/user';
import { BaseReportService, IReportService } from './base-report.service';
import { EventService } from '../event.service';
import { environment } from 'environments/environment';
import { Guid } from 'app/shared/helpers/guid';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json', timeout:'120000' }),
};
@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class ReferralReportService
  extends BaseReportService
   implements IReportService, OnDestroy
{
  // subjectType: SubjectType; // keep track of the current subject type
  currentOptions: ReportOptions; // keep track of currently applied options

  constructor(
    http: HttpClient,
    private categoryService: CategoryService,
    private lookupService: LookupService,
    private statusService: StatusService,
    private organisationService: OrganisationService,
    private eventService: EventService,
    protected userService: UserService,
    protected lookupTypeService: LookupTypeService,
    protected router: Router
  ) {
    super(
      ReferralReportService,
      http,
      'api/referralreports',
      'referralreports',
      router,
      lookupTypeService,
      userService
    );

    this.cacheEnabled = true;
  }

  ngOnDestroy(): void {
    
  }

  itemChanged(val: any) {
    this.onItemChanged.next(val);
    this.eventService.reportItemChanged(val);
  }

  getAllData(
    type?: string,
    urlParams?: string,
    forceRefresh?: boolean
  ): Observable<Report[]> {
    return super.getAllData(type, urlParams, forceRefresh).pipe(
      map((reports) => {
        reports = reports.filter(
          (i) => i.reportCategory === enumReportCategory.Referral
        );

        // add client reports
        reports.push(...this.getClientReports());

        return this.updateCache(reports);
      })
    );
  }

  getReportData(report: Report, reportOptions: ReportOptions): void {
    this.currentOptions = reportOptions;            
    if (report && report.config) {
      const url = this.getReportUrl(report);
      const options = this.getOptions(report, reportOptions);

      if (url && options) {
        this.http
          .post<ReportData>(url, options, httpOptions)
          .subscribe((data: ReportData) => {
            this.eventService.reportDataChanged({
              reportData: data,
            });
          });
      }
    }
  }

  private getReportUrl(report: Report): string {
    if (report && report.config) {
      switch (report.config.reportType) {
        case enumReportType.ConcernsByChannelSource:
          return 'api/referralreports/channelsource';
        case enumReportType.ConcernsByStatus:
          return 'api/referralreports/concernstatus';
        case enumReportType.ConcernsByType:
          return 'api/referralreports/concerntype';
        case enumReportType.WhistleblowerReportSource:
          return 'api/referralreports/whistleblowersource';
        case enumReportType.ConfidentialityBreakdown:
          return 'api/referralreports/confidentialitybreakdown';
        case enumReportType.SatisfactionOutcome:
          return 'api/referralreports/satisfactionoutcome';

        case enumReportType.ReportFindingsPerEmployee:
          return 'api/referralreports/reportfindingperemployee';
        case enumReportType.AnonymousFollowUp:
          return 'api/referralreports/anonymousfollowup';
        case enumReportType.IncidentvsReportDate:
          return 'api/referralreports/incidentvsreportdate';

        // by default return the url for first report, which is by channel source
        default:
          return 'api/referralreports/channelsource';
      }
    } else {
      return undefined;
    }
  }

  getOptions(report: Report, options: ReportOptions) {
    if (report && options) {
      return {
        groupBy: report.config.groupBy,
        value: report.config.valueId,
        aggregate: report.config.aggregate,
        stateFilter: options.state,
        dateFilter: options.dateType,
        dateFrom: options.dateFrom,
        dateTo: options.dateTo,
        includeArchived: options.includeArchived,
        returnType: report.config.returnType,
        organisationFieldId: report.config.organisationFilterId,
        organisations: options.organisations,
        filterFieldId: report.config.filterId,
        filters: options.filters,
        subFilterFieldId: report.config.subFilterId,
        subFilters: options.subFilters,
        userFieldId: report.config.userFilterId,
        users: options.users,
        reportType : options.reportType,
      };
    }
    return {};
  }

  private getLookupTypeId(description): number{
    const lookupType = this.lookupTypeService.getByDescription(description);
    return lookupType ? lookupType.id : undefined;
  }

  private getClientReports(): Report[] {
    const reports: Report[] = [];

    // add concerns by channel source
    // information source lookuptype id = 3, so use this as the filterDataType
    reports.push(
      this.addClientReport(
        enumReportType.ConcernsByChannelSource,
        'Concerns by Channel Source',
        'Count of Concerns by Channel Source',
        'infoSource',
        'Channel Sources',
        (this.getLookupTypeId('Information Source') || 3).toString()
      )
    );

    // add concerns by status
    reports.push(
      this.addClientReport(
        enumReportType.ConcernsByStatus,
        'Concerns by Status',
        'Count of Concerns by Status',
        'StatusSource',
        'Statuses',
        'status'
      )
    );

    reports.push(
      this.addClientReport(
        enumReportType.ConcernsByType,
        'Concerns by Type',
        'Count of Concerns by Type',
        'ConcernSource',
        'Concern Types',
        (this.getLookupTypeId('Case Type') || 2).toString()
      )
    );

    reports.push(
      this.addClientReport(
        enumReportType.WhistleblowerReportSource,
        'Concerns by Whistleblower Source',
        'Count of Concerns by Whistleblower Source',
        'WhistleblowersSource',
        'Whistleblower Sources',
        'whistleblower'
      )
    );

    reports.push(
      this.addClientReport(
        enumReportType.ConfidentialityBreakdown,
        'Concerns by Confidentiality Breakdown',
        'Count of Concerns by Confidentiality Breakdown',
        'ConfidentialityBreakdownSource',
        'Confidentialty Breakdown',
        'confidential'
      )
    );

    reports.push(
      this.addClientReport(
        enumReportType.SatisfactionOutcome,
        'Concerns by Satisfaction Outcome',
        'Count of Concerns by Satisfaction Outcome',
        'SatisfactionOutcomeSource',
        'Satisfaction Outcome',
        (this.getLookupTypeId('Outcome Type') || 5).toString()
      )
    );

    if (this.userService.getCurrentUser().isAdmin === true) {
      var r = this.addClientReport(
        enumReportType.ReportFindingsPerEmployee,
        'Findings Per Employee',
        'Findings Per Employee',
        'FindingsPerEmployeeSource',
        'Show Per N Staff',
        'findingsperemployee'
      );
      r.config.showChart = false;
      r.config.showData = true;
      r.config.options = new ReportOptions();
      r.config.options.includeArchived = false;
      r.config.options.showOrganisations = true;
      r.config.options.showUsers = false;
      r.config.options.showSubFilters = false;
      r.config.options.organisationIncludeBlank = false;
      r.config.options.filters = '1';
      r.config.options.organisations = null;
      r.config.options.showUsers = false;
      reports.push(r);

      r = this.addClientReport(
        enumReportType.AnonymousFollowUp,
        'Anonymous Follow Up',
        'Anonymous Follow Up',
        'AnonymousFollowUpSource',
        '1',
        '1'
      );
      r.config.showChart = false;
      r.config.showData = true;
      r.config.options = new ReportOptions();
      r.config.options.includeArchived = false;
      r.config.options.showOrganisations = true;
      r.config.options.showUsers = false;
      r.config.options.showSubFilters = false;
      r.config.options.organisationIncludeBlank = false;
      r.config.options.showUsers = false;
      reports.push(r);

      r = this.addClientReport(
        enumReportType.IncidentvsReportDate,
        'Incident Date vs Report Date',
        'Incident Date vs Report Date',
        'IncidentvsReportDateSource',
        '2',
        '2'
      );
      r.config.showChart = false;
      r.config.showData = true;
      r.config.options = new ReportOptions();
      r.config.options.includeArchived = false;
      r.config.options.showOrganisations = true;
      r.config.options.showUsers = false;
      r.config.options.showSubFilters = false;
      r.config.options.organisationIncludeBlank = false;
      r.config.options.showUsers = false;
      reports.push(r);
    }

    return reports;
  }

  private addClientReport(
    reportType: enumReportType,
    name: string,
    description: string,
    groupBy: string,
    filterLabel: string,
    filterDataType: string
  ): Report {
    return new Report({
      uid: Guid.newGuid(),
      name,
      description,
      reportType: 'Client Report',
      reportCategory: enumReportCategory.Referral,
      config: new ReportConfig({
        filterLabel,
        filterDataType,
        returnType: enumGroupReportReturnType.Referral,
        reportType,
        groupBy,
      }),
      canDelete: false,
    });
  }

  getFilters(dataType: string): Observable<any> {
    let source$: Observable<any[]>;
    switch (dataType.toLowerCase()) {
      case 'category':
        source$ = this.categoryService.getCategoriesBySubjectType(
          this.subjectType.id || 1
        );
        break;

      case 'status':
        source$ = this.statusService.getAllData('Status');
        break;

      case 'user':
        // MRD - replaced getActiveUsers() with getAllData() so all available users are returned as agreed with PD, 11/03/2020
        source$ = this.userService.getAllData();
        break;

      case 'organisation':
        source$ = this.organisationService.getAllData();
        break;

      case 'findingsperemployee':
        const findingsperemployeeArr = [
          { id: 1, description: '100' },
          { id: 2, description: '1000' },
        ];
        source$ = of(findingsperemployeeArr);
        break;

      case 'whistleblower':
        const whistleblowerArr = [
          { id: 1, wdescription: 'Colleague' },
          { id: 2, wdescription: 'Intranet' },
          { id: 3, wdescription: 'Other' },
          { id: 4, wdescription: 'Policy' },
          { id: 5, wdescription: 'Posters' },
          { id: 6, wdescription: 'Training' },
        ];
        source$ = of(whistleblowerArr);
        break;

      case 'confidential':
        const confidentialArr = [
          { id: true, description: 'Anonymous' },
          { id: false, description: 'Confidential' },
        ];
        source$ = of(confidentialArr);
        break;

      // remove this later as this is just for initial testing.
      // case 'satisfy':
      //   const satisfyArr = [
      //     { id: 11, description: 'Satisfied' },
      //     { id: 12, description: 'Unsatisfied' },
      //   ];
      //   source$ = of(satisfyArr);
      //   break;

      default:
        if (/^\d+$/.test(dataType)) {
          source$ = this.lookupService.getAllByLookupType(Number(dataType));
        } else {
          source$ = this.lookupService.getAllByLookupTypeDescription(dataType);
        }
        break;
    }

    if (source$) {
      return source$.pipe(
        map((data) => {
          const filterList = [];
          if (data.length > 0 && dataType.toLowerCase() === 'user') {
            data = data.map((user) => new User(user));
            const validOrganisations = this.getValidOrganisationIdsForCurrentUser();
            if (validOrganisations) {
              data = data.filter((i) => validOrganisations.indexOf(i.organisationId)>-1);
            }
          }
          if ((data.length > 0) && (dataType.toLowerCase() === 'organisation')) {
            const validOrganisations = this.getValidOrganisationIdsForCurrentUser();
            if (validOrganisations) {
              data = data.filter((i) => validOrganisations.indexOf(i.id)>-1);
            }
          }
          data.forEach((item) => {
            if (item.wdescription) {
              filterList.push({
                filterValue: item.wdescription,
                filterName: item.wdescription,
              });
            } else {
              filterList.push({
                filterValue: item.id,
                filterName: item.description || item.name || item.fullName,
              });
            }
          });
          // tslint:disable-next-line:only-arrow-functions
          return filterList.sort(function (a, b) {
            return a.filterName.localeCompare(b.filterName);
          });
        })
      );
    }
  }

  getValidOrganisationIdsForCurrentUser(): number[]{
    // only need to check valid organisations if LimitAccessToSubjectsByOrg is set to true
    if (environment.limitAccessToSubjectsByOrg === true) {

      // get current user if required
      if (!this.currentUser) {
        this.currentUser = this.userService.getCurrentUser();
      }


      if (this.currentUser){
        if  ((!environment.currentUserIsMultiOrgAdmin) && this.currentUser.organisationId) {
          return [this.currentUser.organisationId];
        }
        else{
          if (this.currentUser.userOrganisations && this.currentUser.userOrganisations.length>0) {
            return [...this.currentUser.userOrganisations.map(i => i.organisationId)];
          }
        }
      }
    }

    // if get here then for whatever reason we could not work out valid organisation(s), return undefined
    return undefined;
  }
}
